import { getAccessToken } from './auth0-client';

/** One workspace the caller belongs to, as returned by subscription-manager. */
export interface MyWorkspace {
  id: string;
  shortName: string;
  subscriptionPlan: string;
  isBillingOwner: boolean;
  federatedLoginDisabled: boolean;
}

interface MyWorkspacesResponse {
  workspaces: MyWorkspace[];
}

/**
 * Fetches the authenticated caller's own workspaces. Mirrors fetchMyEntitlements:
 * the user's bearer token from getAccessToken() + the subscription-manager base
 * URL (passed by the caller so each app supplies its own configured URL).
 */
export async function fetchMyWorkspaces(baseUrl: string): Promise<MyWorkspace[]> {
  const token = await getAccessToken();
  if (!token) throw new Error('No authenticated session');
  const response = await fetch(`${baseUrl}/v1/sponsorship/my-workspaces`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Get user workspaces request failed with status ${response.status}`);
  const body = (await response.json()) as MyWorkspacesResponse;
  return body.workspaces;
}

function decodeAccessTokenClaim(token: string | undefined, claim: string): string | null {
  if (!token) return null;
  try {
    const encodedPayload = token.split('.')[1];
    const decodedPayload = atob(encodedPayload.replace(/-/g, '+').replace(/_/g, '/'));
    const parsedPayload = JSON.parse(decodedPayload);
    return parsedPayload?.[claim] ?? null;
  } catch (err) {
    console.error(`Failed to extract ${claim} from access token`, err);
    return null;
  }
}

/** Decodes the workspace id out of an Auth0 access token's payload. */
export function getWorkspaceFromAccessToken(token?: string): string | null {
  return decodeAccessTokenClaim(token, 'workspace');
}

/** Decodes the subject (the account the token belongs to) out of the payload.
 *  Business Cloud gives each workspace its own account, so this changes on a
 *  workspace switch as well as on a different person signing in. */
export function getSubjectFromAccessToken(token?: string): string | null {
  return decodeAccessTokenClaim(token, 'sub');
}
